import { t } from "@lingui/core/macro";
import { Autocomplete, AutocompleteProps } from "@heroui/react";
import { MantineFormValues } from "./shared.types";
import { isMobileSafari } from "react-device-detect";
import {
  useInfiniteScroll,
  UseInfiniteScrollProps,
} from "@heroui/use-infinite-scroll";
import { useState } from "react";
import { isNativePlatform } from "@/utils/native";

export type LimeAutocompleteProps<T extends object> = MantineFormValues &
  Omit<AutocompleteProps<T>, "scrollRef"> & {
    emptyContentMessage?: string; // Allow customization of the empty content message
    infiniteScrollProps?: Pick<
      UseInfiniteScrollProps,
      "hasMore" | "onLoadMore"
    >;
  };

export const LimeAutocomplete = <T extends object>({
  emptyContentMessage = t`Ni rezultatov`, // Default translated message
  listboxProps,
  error,
  infiniteScrollProps,
  ...props
}: LimeAutocompleteProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleLoadMore = infiniteScrollProps?.onLoadMore;

  const [, scrollerRef] = useInfiniteScroll({
    hasMore: infiniteScrollProps?.hasMore,
    isEnabled: isOpen,
    shouldUseLoader: false,
    onLoadMore: handleLoadMore,
  });

  return (
    <Autocomplete<T>
      errorMessage={error}
      isInvalid={!!error}
      aria-label={
        (typeof props.label === "string" ? props.label : undefined) ??
        "Autocomplete"
      }
      {...props}
      inputProps={{
        ...(props.inputProps || {}),
        style: {
          ...(props.inputProps?.style || {}),
          fontSize: isMobileSafari || isNativePlatform() ? "16px" : undefined,
        },
      }}
      popoverProps={{
        shouldBlockScroll: false,
        ...(props.popoverProps || {}),
      }}
      listboxProps={{
        ...(listboxProps || {}),
        emptyContent: emptyContentMessage, // Set the default or overridden message
      }}
      onOpenChange={(isOpen) => {
        setIsOpen(isOpen);
        props.onOpenChange?.(isOpen);
      }}
      scrollRef={infiniteScrollProps ? scrollerRef : undefined}
    >
      {props.children}
    </Autocomplete>
  );
};
